/**
 * Rate Cards Management Page - Industry Standard Pricing
 * Like ServiceTitan/Jobber price book management
 */

import { showToast } from '../../components/Common/Toast';
import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../contexts/UserContext';
import { RateCardService } from '../../services/RateCardService';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  TagIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Board #3235. The storage ceiling of rate_cards.rate — numeric(10,2), verified live
// 2026-09-28 — NOT a product cap on what a contractor may charge (#3770 rules there is none).
const RATE_MAX = 99999999.99;

const RateCards = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [rateCards, setRateCards] = useState([]);
  const [groupedRateCards, setGroupedRateCards] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  // #5564 (ruling 2026-10-14): inactive = archived, listed only when asked for.
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (user?.company_id) {
      loadRateCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.company_id, showInactive]);

  const loadRateCards = async () => {
    try {
      setLoading(true);
      const cards = await RateCardService.getRateCards(user.company_id, { includeInactive: showInactive });
      setRateCards(cards);
      
      // Group by category
      const grouped = await RateCardService.getRateCardsByCategory(user.company_id, { includeInactive: showInactive });
      setGroupedRateCards(grouped);
    } catch (error) {
      console.error('Error loading rate cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetActive = async (card, active) => {
    try {
      await RateCardService.setRateCardActive(card.id, active);
      showToast('success', active ? `"${card.service_name}" is active again` : `"${card.service_name}" archived`);
      loadRateCards();
    } catch (error) {
      console.error('Error changing rate card status:', error);
      showToast('error', 'Could not change the rate card status');
    }
  };

  const handleAddCard = () => {
    setEditingCard(null);
    setShowAddModal(true);
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setShowAddModal(true);
  };

  // Themed confirmation instead of window.confirm. The native dialog returned false when
  // dismissed and the handler returned immediately, which is indistinguishable from a dead
  // button: no request, no toast, nothing. It also put an OS dialog in the middle of a
  // themed settings page, and automated drivers auto-dismiss native dialogs, so this path
  // could never be tested.
  const [pendingDelete, setPendingDelete] = useState(null);

  const confirmDeleteCard = async () => {
    const cardId = pendingDelete?.id;
    setPendingDelete(null);
    if (!cardId) return;
    try {
      await RateCardService.deleteRateCard(cardId);
      await loadRateCards(); // Refresh
      showToast('success', 'Rate card deleted');
    } catch (error) {
      console.error('Error deleting rate card:', error);
      showToast('error', 'Failed to delete rate card');
    }
  };

  const filteredCards = selectedCategory === 'ALL' 
    ? rateCards 
    : rateCards.filter(card => card.category === selectedCategory);

  const categories = RateCardService.getServiceCategories();
  const unitTypes = RateCardService.getUnitTypes();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header — ACTION ONLY. The title and description are already rendered by
          the Settings chrome (pages/Settings.js: breadcrumb, <h2>, description),
          so repeating them here printed "Rate Cards" twice directly above itself
          and put a second <h1> in a document that already had one — an H1 → H2 →
          H1 order in which the subsection outranked the chrome containing it.
          This component is only ever mounted inside that chrome. */}
      <div className="mb-8">
        <div className="flex items-center justify-end gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show inactive
          </label>
          <button
            onClick={handleAddCard}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            Add Rate Card
          </button>
        </div>

        {/* Category Filter */}
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('ALL')}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              selectedCategory === 'ALL'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({rateCards.length})
          </button>
          {categories.map(category => {
            const count = rateCards.filter(card => card.category === category.value).length;
            if (count === 0) return null;
            
            return (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedCategory === category.value
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Rate Cards Grid */}
      {filteredCards.length === 0 ? (
        <div className="text-center py-12">
          <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No rate cards</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first rate card.
          </p>
          <div className="mt-6">
            <button
              onClick={handleAddCard}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Add Rate Card
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,18rem),1fr))] gap-6">
          {filteredCards.map(card => (
            <div key={card.id} className="min-w-0 bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  {/* #1655/#3934: truncate a long name (full text on hover); the grid sizes to
                      the settings panel, not the viewport, so cards never overflow it. */}
                  <h3 className="text-lg font-medium text-gray-900 truncate" title={card.service_name}>
                    {card.service_name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <TagIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {categories.find(c => c.value === card.category)?.label}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {/* Icon-only, so the name has to be spoken. Both were announced as
                      just "button" before. The card's service name is included so a
                      screen-reader user knows WHICH card they are about to change. */}
                  <button
                    onClick={() => handleEditCard(card)}
                    aria-label={`Edit ${card.service_name}`}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <PencilIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => setPendingDelete(card)}
                    aria-label={`Delete ${card.service_name}`}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              </div>

              {/* Description */}
              {card.description && (
                <p className="text-sm text-gray-600 mb-4">{card.description}</p>
              )}

              {/* Pricing */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-900">
                  {RateCardService.formatRateDisplay(card.rate, card.unit_type)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {unitTypes.find(u => u.value === card.unit_type)?.label}
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  {card.is_active ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-700 dark:text-green-600">Active</span>
                    </>
                  ) : (
                    <>
                      <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-yellow-600">Inactive</span>
                    </>
                  )}
                </div>
                {!card.is_active && (
                  <button
                    type="button"
                    onClick={() => handleSetActive(card, true)}
                    className="text-xs font-medium text-blue-700 hover:underline"
                    aria-label={`Reactivate ${card.service_name}`}
                  >
                    Reactivate
                  </button>
                )}
                {card.is_default && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    Default
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {/* Shared ConfirmDialog rather than the one hand-rolled here earlier today. Two
          implementations of the same prompt is how they drift -- this one already lacked
          the focus handling the shared component has. */}
      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this rate card?"
        message={pendingDelete
          ? `${pendingDelete.service_name} will be removed from your rate book. Quotes that already used it keep the price they were given.`
          : ''}
        confirmLabel="Delete rate card"
        tone="danger"
        onConfirm={confirmDeleteCard}
        onCancel={() => setPendingDelete(null)}
      />

      {showAddModal && (
        <RateCardModal
          card={editingCard}
          onClose={() => setShowAddModal(false)}
          onSave={loadRateCards}
          companyId={user.company_id}
          existingCards={rateCards}
        />
      )}
    </div>
  );
};

// Rate Card Modal Component
const RateCardModal = ({ card, onClose, onSave, companyId, existingCards = [] }) => {
  const [formData, setFormData] = useState({
    service_name: card?.service_name || '',
    description: card?.description || '',
    category: card?.category || 'OTHER',
    unit_type: card?.unit_type || 'FLAT_FEE',
    // ?? not || — a stored rate of 0 opened the edit form with an empty required field (#1645).
    rate: card?.rate ?? '',
    min_quantity: card?.min_quantity || 1,
    max_quantity: card?.max_quantity || '',
    is_active: card?.is_active !== false,
    is_default: card?.is_default || false,
    sort_order: card?.sort_order || 0
  });
  const [saving, setSaving] = useState(false);
  // Board #1644: set when the user has been shown the duplicate-name warning once.
  const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false);
  const dialogRef = useRef(null);

  // Escape closes the dialog. There was no key handler of any kind, so the only
  // way out was the Cancel button — a keyboard user who opened this had no way
  // to leave it. Listening on the document rather than the dialog node because
  // focus starts on <body> until something inside is focused, and a keydown
  // there would never reach a handler bound to the panel.
  //
  // Guarded on `saving`: dismissing mid-write would hide a request that is still
  // going to land, and the row would appear from nowhere on the next load.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, saving]);

  // Move focus into the dialog on open, so the first Tab lands inside it rather
  // than continuing through the page behind.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const categories = RateCardService.getServiceCategories();
  const unitTypes = RateCardService.getUnitTypes();

  /**
   * Board #1644. Jerry's ruling 2026-09-21: "Exact duplicate service names trigger a
   * warning but intentional variants remain possible." So this warns and then lets you
   * through on a second, explicit action — it does not block. Scoped to ACTIVE cards,
   * case-insensitively, excluding the card being edited: an inactive card with the same
   * name is not a collision a user can see, and re-saving a card is not a duplicate of
   * itself.
   */
  const duplicateCard = (() => {
    const typed = String(formData.service_name || '').trim().toLowerCase();
    if (!typed) return null;
    // #5797: an inactive card is not a visible collision, and an edit that keeps the card's
    // own name was already accepted once — deactivating or re-pricing it re-warned before.
    if (formData.is_active === false) return null;
    if (card && String(card.service_name || '').trim().toLowerCase() === typed) return null;
    return existingCards.find(existing => (
      existing.id !== card?.id
      && existing.is_active !== false
      && String(existing.service_name || '').trim().toLowerCase() === typed
    )) || null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const serviceName = String(formData.service_name || '').trim();
    if (!serviceName) {
      showToast('error', 'Service name is required');
      return;
    }

    if (duplicateCard && !duplicateAcknowledged) {
      setDuplicateAcknowledged(true);
      return;
    }

    setSaving(true);

    try {
      // COERCE AT THE SAVE BOUNDARY. min_quantity, max_quantity and sort_order are
      // INTEGER columns, and max_quantity is seeded to '' when a card has no maximum --
      // an empty <input type="number"> also yields ''. Postgres rejects that with
      // 22P02 "invalid input syntax for type integer", which is the 400 that made Edit
      // Rate Card impossible to save. All three are nullable, so empty means NULL.
      //
      // `rate` is numeric and goes the same way rather than being left to send ''.
      const toIntOrNull = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));
      const payload = {
        ...formData,
        service_name: serviceName,
        rate: formData.rate === '' || formData.rate === null ? null : Number(formData.rate),
        min_quantity: toIntOrNull(formData.min_quantity),
        max_quantity: toIntOrNull(formData.max_quantity),
        sort_order: toIntOrNull(formData.sort_order) ?? 0
      };

      if (card) {
        await RateCardService.updateRateCard(card.id, payload);
      } else {
        await RateCardService.createRateCard(companyId, payload);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving rate card:', error);
      showToast('error', 'Failed to save rate card');
    } finally {
      setSaving(false);
    }
  };

  // Clicking the backdrop also closes, which is the other half of what a
  // keyboard user expects from Escape.
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rate-card-modal-title"
        tabIndex={-1}
        className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 max-w-2xl w-full max-h-[90vh] overflow-y-auto focus:outline-none"
      >
        <div className="p-6">
          <h2 id="rate-card-modal-title" className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            {card ? 'Edit Rate Card' : 'Add Rate Card'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Service Name */}
            <div>
              <label htmlFor="rate-card-service-name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Service Name *
              </label>
              <input
                id="rate-card-service-name"
                type="text"
                maxLength={120}
                value={formData.service_name}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, service_name: e.target.value }));
                  // Editing the name re-arms the warning, so acknowledging one duplicate
                  // does not silently cover a different one typed afterwards.
                  setDuplicateAcknowledged(false);
                }}
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
              {duplicateCard && (
                <div role="alert" className="mt-2 rounded-lg border border-amber-300/70 bg-amber-50 p-3 text-sm dark:border-amber-500/40 dark:bg-amber-900/20">
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    You already have an active rate card called "{duplicateCard.service_name}".
                  </p>
                  <p className="mt-1 text-amber-700 dark:text-amber-300">
                    {duplicateAcknowledged
                      ? `${card ? 'Save' : 'Create'} again to keep both — deliberate variants are allowed.`
                      : 'Give this one a more specific name, or save to keep both.'}
                  </p>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="rate-card-description" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Description
              </label>
              <textarea
                id="rate-card-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Category and Unit Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="rate-card-category" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Category *
                </label>
                <select
                  id="rate-card-category"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="rate-card-unit-type" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Unit Type *
                </label>
                <select
                  id="rate-card-unit-type"
                  value={formData.unit_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit_type: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  {unitTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Rate */}
            <div>
              <label htmlFor="rate-card-rate" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Rate *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                </div>
                {/* Board #3235. The field had min but no max, so a rate above what the
                    column can hold was accepted by the form and refused by Postgres as a
                    22003 numeric overflow — a raw error for something the form could have
                    said plainly.
                    RATE_MAX is the STORAGE ceiling of rate_cards.rate, numeric(10,2),
                    verified live 2026-09-28. It is deliberately not a product cap: #3770
                    rules that there is no maximum a contractor may charge, and the settled
                    entry on percentage precision already says the column type is storage
                    capacity rather than the product limit. This only refuses what cannot
                    be stored. */}
                <input
                  id="rate-card-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max={RATE_MAX}
                  placeholder="0.00"
                  value={formData.rate}
                  onChange={(e) => setFormData(prev => ({ ...prev, rate: e.target.value }))}
                  className="w-full pl-7 pr-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {/* Labels already say "Per Hour"; prefixing "Per" printed "Per per hour" (#4184). */}
                  {unitTypes.find(u => u.value === formData.unit_type)?.label || 'Per unit'}. Up to ${RATE_MAX.toLocaleString()}.
                </p>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-white/20 dark:border-slate-600 text-primary-600 focus:ring-primary-500 bg-white/70 dark:bg-slate-800/60"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">Active</span>
              </label>
              {!formData.is_active && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Inactive cards are hidden from quotes. Find them again with "Show inactive".
                </p>
              )}

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="rounded border-white/20 dark:border-slate-600 text-primary-600 focus:ring-primary-500 bg-white/70 dark:bg-slate-800/60"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">Default for category</span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-slate-700/40">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700/40 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700/60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saving
                  ? 'Saving...'
                  : (duplicateCard && duplicateAcknowledged
                    ? (card ? 'Update Anyway' : 'Create Anyway')
                    : (card ? 'Update' : 'Create'))}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RateCards;

